import React, { useMemo } from "react"
import "./PPStatistics.css"
import { useQuery } from "@apollo/client"
import { useSearchParams } from "react-router-dom"
import PPStatisticsInsights from "../../utils/PPStatisticsInsights"
import { useTranslation } from "react-i18next"
import PPLoadingIndicator from "../../components/layout/PPLoadingIndicator"
import "../../styles/dashboard.css"
import { STATS_QUERY } from "./statisticsQueries"
import { KpiStrip } from "./KpiStrip"
import {
    StackedShopChart,
    MarketActivitySummary,
    VolatilityChart,
    TrendDistributionDonut,
    CategoryRisingChart,
    CategoryFallingChart,
    PriceBalancePanel,
    CompetitorCheaperChart,
    OpportunityPanel,
    StockHistoryChart,
    ChartProductMapping,
    CrawlCharts
} from "./statisticsCharts"
import PPErrorBoundary from "../../components/layout/PPErrorBoundary"

const STAT_TABS = ["overview", "movement", "gaps", "opportunities", "crawler"]

export default function PPStatistics() {
    const [t, i18n] = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()
    const tabFromUrl = searchParams.get("tab")
    const activeTab = STAT_TABS.includes(tabFromUrl) ? tabFromUrl : "overview"

    const selectTab = (tabId) => {
        const next = new URLSearchParams(searchParams)
        if (tabId === "overview") {
            next.delete("tab")
        } else {
            next.set("tab", tabId)
        }
        setSearchParams(next)
        window.dispatchEvent(new Event("resize"))
    }

    const variables = {
        trendDays: 7
    }

    const { loading, error, data } = useQuery(STATS_QUERY, {
        fetchPolicy: "no-cache",
        variables
    })

    const mainLang = data?.baseData?.mainLang || "de"

    const shopStats = data?.stats?.shopStats || []
    const competitorShops = useMemo(() => PPStatisticsInsights.competitorShops(shopStats), [shopStats])

    const stockSeries = (shops) => [
        {
            name: t("statistic.stock_available"),
            data: shops.map((shop) => ({
                x: PPStatisticsInsights.shopLabel(shop),
                y: shop.countStockGreenProducts
            }))
        },
        {
            name: t("statistic.stock_limited"),
            data: shops.map((shop) => ({
                x: PPStatisticsInsights.shopLabel(shop),
                y: shop.countStockYellowProducts
            }))
        },
        {
            name: t("statistic.stock_unavailable"),
            data: shops.map((shop) => ({
                x: PPStatisticsInsights.shopLabel(shop),
                y: shop.countStockRedProducts
            }))
        }
    ]

    const priceSeries = (shops) => [
        {
            name: t("statistic.price_cheaper"),
            data: shops
                .filter((shop) => shop.shopId !== 0)
                .map((shop) => ({
                    x: PPStatisticsInsights.shopLabel(shop),
                    y: shop.productsPriceLower
                }))
        },
        {
            name: t("statistic.price_equal"),
            data: shops
                .filter((shop) => shop.shopId !== 0)
                .map((shop) => ({
                    x: PPStatisticsInsights.shopLabel(shop),
                    y: shop.productsPriceEqual
                }))
        },
        {
            name: t("statistic.price_expensive"),
            data: shops
                .filter((shop) => shop.shopId !== 0)
                .map((shop) => ({
                    x: PPStatisticsInsights.shopLabel(shop),
                    y: shop.productsPriceHigher
                }))
        }
    ]

    if (loading) return <PPLoadingIndicator />
    if (error) return <p>Error :(</p>
    if (!data) return null

    const tabs = [
        { id: "overview", label: t("statistic.tab_overview") },
        { id: "movement", label: t("statistic.tab_movement") },
        { id: "gaps", label: t("statistic.tab_gaps") },
        { id: "opportunities", label: t("statistic.tab_opportunities") },
        { id: "crawler", label: t("statistic.tab_crawler") }
    ]

    return (
            <div className="card shadow card-primary card-outline card-outline-tabs mb-3">
                <div className="card-header p-0 border-bottom-0">
                    {data?.stats?.lastUpdate && (
                        <div className={"stat_lastdate"}>
                            {t("single.stat_last_update")}:{" "}
                            {new Intl.DateTimeFormat(i18n.language, {
                                year: "numeric",
                                month: "long",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit"
                            }).format(new Date(data?.stats?.lastUpdate))}
                        </div>
                    )}
                    <ul className="nav nav-tabs" role="tablist">
                        {tabs.map((tab) => (
                            <li className="nav-item" key={tab.id}>
                                <a
                                    className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                                    href={tab.id === "overview" ? "/statistics" : `/statistics?tab=${tab.id}`}
                                    role="tab"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        selectTab(tab.id)
                                    }}>
                                    {tab.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="card-body">
                    <PPErrorBoundary variant="section" resetKeys={[activeTab]}>
                    {activeTab === "overview" && (
                        <>
                            <KpiStrip stats={data.stats} t={t} />
                            <div className="row">
                                <div className="col-lg-6 mb-3">
                                    <StackedShopChart
                                        id="selectStatisticsPricedifference"
                                        title={t("statistic.price_position_title")}
                                        shops={competitorShops}
                                        seriesBuilder={priceSeries}
                                    />
                                </div>
                                <div className="col-lg-6 mb-3">
                                    <StackedShopChart
                                        id="selectStatisticsStock"
                                        title={t("statistic.stock_availability_title")}
                                        shops={competitorShops}
                                        seriesBuilder={stockSeries}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <MarketActivitySummary
                                        shopStats={shopStats}
                                        trendByShop={data.stats?.priceTrendStatistics?.trendByShop}
                                        t={t}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === "movement" && (
                        <div className="row">
                            <div className="col-lg-6 mb-3">
                                <VolatilityChart
                                    trendByShop={data.stats?.priceTrendStatistics?.trendByShop}
                                    shops={data.shops}
                                    i18n={i18n}
                                    t={t}
                                />
                            </div>
                            <div className="col-lg-6 mb-3">
                                <TrendDistributionDonut
                                    trendByShop={data.stats?.priceTrendStatistics?.trendByShop}
                                    t={t}
                                />
                            </div>
                            <div className="col-lg-6 mb-3">
                                <CategoryRisingChart
                                    shopStats={shopStats}
                                    department=""
                                    t={t}
                                />
                            </div>
                            <div className="col-lg-6 mb-3">
                                <CategoryFallingChart
                                    shopStats={shopStats}
                                    department=""
                                    t={t}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "gaps" && (
                        <div className="row">
                            <div className="col-lg-6 mb-3">
                                <PriceBalancePanel
                                    shopStats={shopStats}
                                    gaps={data.stats?.avgPriceGapByShop}
                                    preferredLang={mainLang}
                                    i18n={i18n}
                                    t={t}
                                />
                            </div>
                            <div className="col-lg-6 mb-3">
                                <CompetitorCheaperChart
                                    productsPriceLowerByShops={data.stats?.productsPriceLowerByShops}
                                    shops={data.shops}
                                    i18n={i18n}
                                    t={t}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "opportunities" && (
                        <>
                            <div className="row mb-3">
                                <div className="col-12 mb-3">
                                    <OpportunityPanel
                                        shopStats={shopStats}
                                        t={t}
                                    />
                                </div>
                            </div>
                            <StockHistoryChart
                                history={data.stats?.stockLightHistory}
                                shopStats={shopStats}
                                preferredLang={mainLang}
                                i18n={i18n}
                                t={t}
                            />
                        </>
                    )}

                    {activeTab === "crawler" && (
                        <>
                            <div className="row mb-3">
                                <div className="col-md-6 mb-3">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <ChartProductMapping
                                                id="selectStatisticsChartProductMappingProducts"
                                                title={t("statistic.chart_product_mapping_products")}
                                                data={shopStats}
                                                shopsProductAllCounts={data?.stats?.shopsProductAllCounts}
                                                pushCustomerProducts={data?.stats?.activeProducts}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <ChartProductMapping
                                                id="selectStatisticsChartProductMappingMappings"
                                                title={t("statistic.chart_product_mapping_mappings")}
                                                data={shopStats}
                                                shopsProductAllCounts={data?.stats?.shopsProductMappedCounts}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <CrawlCharts crawlerCountSeries={data?.stats?.crawlerCountSeries} t={t} />
                        </>
                    )}
                    </PPErrorBoundary>
                </div>
            </div>
    )
}
